export class Helpers {
    /**
     * Converts seconds to a minutes and seconds string (e.g., "2m 30s")
     */
    static convertSecondsToMinutes(seconds: number): string {
        if (isNaN(seconds) || seconds < 0) return '00:00'; // Handle invalid input

        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;

        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    // static downloadFile(url: string, fileName: string) {
    //     fetch(url, { mode: 'cors' }) 
    //         .then(response => response.blob())
    //         .then(blob => {
    //             const blobUrl = URL.createObjectURL(blob);
    //             const link = document.createElement('a');
    //             link.href = blobUrl;
    //             link.download = fileName;
    //             document.body.appendChild(link);
    //             link.click();
    //             document.body.removeChild(link);
    //             URL.revokeObjectURL(blobUrl); 
    //         })
    //         .catch(error => console.error('Download error:', error));
    // }

    static downloadFile(url: string, fileName?: string) {
        const link = document.createElement('a');
        link.href = url;

        if (fileName) {
            link.download = fileName;
        }

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    static base64ToBlob(base64: string, contentType = 'application/octet-stream'): Blob {
        const byteCharacters = atob(base64);
        const byteArray = new Uint8Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteArray[i] = byteCharacters.charCodeAt(i);
        }

        return new Blob([byteArray], { type: contentType });
    }

    static downloadBlob(blob: Blob, fileName?: string): void {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    }

    /**
     * Formats a number to 2 decimal places
     * @param value - The number to format
     * @returns The number formatted to 2 decimal places, or 0 if value is null/undefined/NaN
     */
    static formatAmount(value: number | null | undefined): number {
        if (value === null || value === undefined || isNaN(value)) {
            return 0;
        }
        return Number(Number(value).toFixed(2));
    }

    /**
     * Divides a total into equal currency shares rounded to cents.
     * Any residual cents are applied to the final share so the sum matches
     * the normalized total exactly (e.g. 100 / 3 => 33.33, 33.33, 33.34).
     */
    static divideCurrencyEqually(total: number, count: number): number[] {
        const n = Math.floor(Number(count));
        if (!Number.isFinite(n) || n < 1) {
            return [];
        }

        const totalCents = this.toCurrencyCents(total);
        if (n === 1) {
            return [totalCents / 100];
        }
        if (totalCents === 0) {
            return Array.from({ length: n }, () => 0);
        }

        const perShareCents = Math.floor(totalCents / n);
        const lastShareCents = totalCents - perShareCents * (n - 1);

        return Array.from({ length: n }, (_, index) =>
            (index === n - 1 ? lastShareCents : perShareCents) / 100
        );
    }

    private static toCurrencyCents(value: number | null | undefined): number {
        const amount = Number(value);
        if (!Number.isFinite(amount) || amount === 0) {
            return 0;
        }

        const sign = amount < 0 ? -1 : 1;
        const scaled = Number((Math.abs(amount) * 100).toPrecision(15));
        return sign * this.roundHalfToEven(scaled);
    }

    private static roundHalfToEven(value: number): number {
        const floor = Math.floor(value);
        const diff = value - floor;
        if (diff < 0.5) {
            return floor;
        }
        if (diff > 0.5) {
            return floor + 1;
        }
        return floor % 2 === 0 ? floor : floor + 1;
    }

    /**
     * Formats a number to 2 decimal places for display (returns string with $ prefix)
     * @param value - The number to format
     * @returns The formatted string with $ prefix, or empty string if value is null/undefined/NaN
     */
    static formatAmountDisplay(value: number | null | undefined): string {
        if (value === null || value === undefined || isNaN(value)) {
            return '';
        }
        return `$${Number(value).toFixed(2)}`;
    }

    /**
     * Recursively transforms all 'amount' keys in an object to 2 decimal places
     * @param obj - The object to transform
     * @returns The transformed object with all amount values formatted to 2 decimals
     */
    static transformAmountsInObject(obj: any): any {
        if (obj === null || obj === undefined) {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj.map(item => this.transformAmountsInObject(item));
        }

        if (typeof obj === 'object') {
            const transformed: any = {};
            for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                    if (key.toLowerCase() === 'amount' && typeof obj[key] === 'number') {
                        transformed[key] = this.formatAmount(obj[key]);
                    } else {
                        transformed[key] = this.transformAmountsInObject(obj[key]);
                    }
                }
            }
            return transformed;
        }

        return obj;
    }
}